/**
 * Right First Time report (SPEC §8.7, §8.8).
 *
 * Reading order is trend -> the adjustments the rate excludes -> the records
 * behind the selected month: the rate line sets context, the water-only columns
 * show what is counted separately, and the detail table holds the individual
 * adjustment records.
 *
 * This is the reference implementation's `RftReport.tsx` with its data layer
 * removed rather than rewritten. What went:
 *
 * * **Three `useEffect` fetches and their cancellation flags.** Every panel comes
 *   from one payload, so there is nothing to sequence and nothing to cancel. Both
 *   of the reference's review defects were a panel not depending on everything
 *   that changes it — the stale matrix on CAR, and here the filter-options fetch
 *   excluding `site` so the undated count showed the *previous* site's number.
 *   Neither is expressible here: there are no dependency arrays, and the undated
 *   count arrives as a caveat computed for the current site.
 * * **`useState` for the site and the selected month, and the `rft.site`
 *   `localStorage` key.** Both live in the URL now (§9.5), so a drilled-down view
 *   is a link — which the reference explicitly could not do (brief §8, item 3).
 * * **The site `<select>`, the page header, the freshness line and the undated
 *   sentence.** The portal's ParamBar renders the site picker from this payload's
 *   `sites` dataset; the shell renders the title, the "read from source" badge
 *   with its refresh control, and the caveat (§9.3, §9.6).
 *
 * What stayed is everything the reader sees: the rate line with its non-zero axis
 * floor and crosshair, the water-only column chart, and the SKU-chip table.
 */

import type { CustomReportProps } from '..'
import { Card } from '../shared/Card'
import { MonthColumnChart } from '../shared/MonthColumnChart'
import { monthLabel } from '../shared/format'
import { count, dataset, text } from '../shared/rows'

import '../report.css'

import { AdjustmentTable } from './AdjustmentTable'
import { MonthRateChart } from './MonthRateChart'

function asString(value: unknown): string {
  return typeof value === 'string' ? value : ''
}

export function RightFirstTime({ result, values, setParam }: CustomReportProps) {
  const monthly = dataset(result, 'monthly').map((row) => ({
    month: text(row.month) ?? '',
    total: count(row.total),
    adjustments: count(row.adjustments),
    waterOnly: count(row.water_only),
    // `rft_pct` is a real number or null (a month with no records), never 0 by
    // default — a 0% right-first-time month and a month with no data are
    // different statements, and the rate chart breaks its line for the second.
    rft: typeof row.rft_pct === 'number' ? row.rft_pct : null,
  }))
  const detail = dataset(result, 'primary')

  // The selected month comes from the server, not from a local fallback: the
  // server already resolved it to compute the detail rows, so re-deriving it here
  // would be a second implementation of the same rule and the two would disagree
  // the first time a site had no rows in the month sitting in the URL.
  const selectedMonth = text(dataset(result, 'selection')[0]?.month ?? null)

  const site = asString(values.site)
  const totalRecords = monthly.reduce((sum, m) => sum + m.total, 0)
  const totalAdjustments = monthly.reduce((sum, m) => sum + m.adjustments, 0)
  const overallRft = totalRecords > 0 ? (1 - totalAdjustments / totalRecords) * 100 : null

  const selected = monthly.find((m) => m.month === selectedMonth) ?? null
  const detailSubtitle =
    selectedMonth === null
      ? 'No month to show'
      : `${monthLabel(selectedMonth)} · ${detail.length.toString()} ${
          detail.length === 1 ? 'record' : 'records'
        }${
          selected === null
            ? ''
            : ` of ${selected.total.toString()}${
                selected.rft === null ? '' : ` · ${selected.rft.toFixed(1)}% right first time`
              }`
        }`

  return (
    <div className="bespoke-report">
      <p className="report-summary">
        {site === '' ? '' : `${site} · `}
        {totalRecords.toLocaleString()} {totalRecords === 1 ? 'record' : 'records'}
        {overallRft === null ? '' : ` · ${overallRft.toFixed(1)}% right first time overall`}
      </p>

      <Card
        title="Right first time, by month"
        subtitle="Records needing no adjustment, as a share of all records. Water-only adjustments are excluded. Select a month to load its detail below."
      >
        <MonthRateChart
          data={monthly.map((m) => ({
            month: m.month,
            value: m.rft,
            detail: `${(m.total - m.adjustments).toString()} of ${m.total.toString()} records`,
          }))}
          selectedMonth={selectedMonth}
          onSelectMonth={(month) => {
            setParam('month', month)
          }}
        />
      </Card>

      <Card
        title="Water-only adjustments, by month"
        subtitle="Counted separately — these do not reduce the rate above."
      >
        <MonthColumnChart
          data={monthly.map((m) => ({ month: m.month, value: m.waterOnly }))}
          selectedMonth={selectedMonth}
          onSelectMonth={(month) => {
            setParam('month', month)
          }}
          noun={['water-only adjustment', 'water-only adjustments']}
          emptyLabel="No records for this site."
        />
      </Card>

      <Card title="Adjustment detail" subtitle={detailSubtitle}>
        <AdjustmentTable rows={detail} />
      </Card>
    </div>
  )
}
